// Firebase Auth SDK seam — the ONLY module that imports the `firebase`
// package. Mirrors the backend's `_verify_firebase_id_token` isolation: tests
// mock this module wholesale, and dev-token mode (VITE_ADMIN_TOKEN set) never
// loads it, so firebase stays out of the local-dev / test code path entirely.
//
// Everything is behind a lazy dynamic import so the firebase chunk is only
// fetched when a real Google sign-in is actually attempted.

import type { Auth } from "firebase/auth";

/** Minimal user shape the app cares about — firebase's `User` never leaks out. */
export interface FirebaseUser {
  uid: string;
  email: string | null;
}

// Public Firebase web config (client identifiers, NOT secrets — safe to ship
// in the bundle; access is gated by Identity Platform authorized domains +
// the backend `admin:true` claim check). Injected at build time.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let authClient: Promise<Auth> | null = null;

async function getAuthClient(): Promise<Auth> {
  if (!authClient) {
    authClient = (async () => {
      const { initializeApp } = await import("firebase/app");
      const { getAuth } = await import("firebase/auth");
      return getAuth(initializeApp(firebaseConfig));
    })();
  }
  return authClient;
}

/**
 * Subscribe to auth/token changes. Uses `onIdTokenChanged` (not just
 * `onAuthStateChanged`) so a forced token refresh — e.g. right after the
 * `admin:true` claim is granted — re-fires the callback. Returns an
 * unsubscribe function.
 */
export function onAuthChange(cb: (user: FirebaseUser | null) => void): () => void {
  let unsubscribe = (): void => {};
  let cancelled = false;
  void (async () => {
    const auth = await getAuthClient();
    const { onIdTokenChanged } = await import("firebase/auth");
    if (cancelled) return;
    unsubscribe = onIdTokenChanged(auth, (user) =>
      cb(user ? { uid: user.uid, email: user.email } : null),
    );
  })();
  return () => {
    cancelled = true;
    unsubscribe();
  };
}

/** Launch the Google sign-in popup. Throws on user-cancel / network error. */
export async function signInWithGoogle(): Promise<void> {
  const auth = await getAuthClient();
  const { GoogleAuthProvider, signInWithPopup } = await import("firebase/auth");
  await signInWithPopup(auth, new GoogleAuthProvider());
}

/** Sign the current user out. */
export async function signOutUser(): Promise<void> {
  const auth = await getAuthClient();
  const { signOut } = await import("firebase/auth");
  await signOut(auth);
}

/**
 * Current user's Firebase ID token for the `Authorization: Bearer` header, or
 * null when signed out. `forceRefresh` skips the cache — use it once after the
 * admin claim is granted so the new claim lands in the token.
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const auth = await getAuthClient();
  return auth.currentUser ? auth.currentUser.getIdToken(forceRefresh) : null;
}
